// @flow
import React, { Component } from 'react';
import ReactSVG from 'react-svg';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import uuid from 'uuid/v1';
import {
  CustomView,
  isMobileOnly,
  BrowserView,
  MobileView
} from 'react-device-detect';
import parser from 'lucene-query-parser';

import SubHeader from '../../components/SubHeader/index';
import Input from '../../components/Input/index';
import Select from '../../components/Select/index';
import { Wrapper, svgStyle } from './style';
import Button from '../../components/Button';
import close from '../../assets/images/supprimer.svg';
import Checkbox from '../../components/Checkbox';
import YearSelect from '../../components/YearSelect';
import { setCriteria, setDocumentsPerPage } from '../../actions/search';
import { resetFacetState } from '../../actions/ui';

import history from '../../history';

import type {
  Criteria,
  SetCriteria as SetCriteriaType,
  Platforms,
  Years
} from '../../types/Criteria';
import type { Select as SelectType } from '../../types/Select';
import { FormattedMessage } from 'react-intl';
import { formatMessage } from '../../CurrentLocale';

export const optionsPlatforms = [
  { value: 'OJ', label: 'OpenEdition JOURNALS' },
  { value: 'OB', label: 'OpenEdition BOOKS' },
  { value: 'HO', label: 'HYPOTHESES' },
  { value: 'CO', label: 'CALENDA' }
];

type Props = {
  search: Object,
  setCriteria: SetCriteriaType => void,
  setDocumentsPerPage: string => void,
  resetFacetState: () => void,
  history: {
    push: string => void
  }
};

type State = {
  query: string,
  criteria: Criteria,
  access: Array<string>,
  platforms: Platforms,
  years: Years,
  screenWidth: number
};

class Result extends Component<Props, State> {
  state = {
    query: '',
    criteria: [],
    access: [],
    platforms: {
      OJ: false,
      OB: false,
      HO: false,
      CO: false
    },
    years: {
      min: 1970,
      max: parseInt(moment().format('YYYY'), 0),
      mustUse: false
    },
    screenWidth: 0
  };

  options: any = [];
  conditions: any = [];

  constructor(props) {
    super(props);

    this.options = [
      { value: 'title', label: formatMessage({ id: 'advancedSearch.title' }) },
      {
        value: 'author',
        label: formatMessage({ id: 'advancedSearch.author' })
      },
      {
        value: 'abstract',
        label: formatMessage({ id: 'advancedSearch.overview' })
      },
      { value: 'text', label: formatMessage({ id: 'advancedSearch.text' }) }
    ];

    this.conditions = [
      { value: 'AND', label: formatMessage({ id: 'conditions.AND' }) },
      { value: 'OR', label: formatMessage({ id: 'conditions.OR' }) },
      //{ value: 'EXCEPT', label: formatMessage({ id: 'conditions.EXCEPT' }) }
    ];
  }

  unlisten = () => {};

  updateDimensions = () => {
    this.setState({
      screenWidth: Math.max(
        //$FlowFixMe
        document.documentElement.clientWidth,
        window.innerWidth || 0
      )
    });
  };

  componentDidMount() {
    this.autoFill();
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    if (this.unlisten) this.unlisten();
    window.removeEventListener('resize', this.updateDimensions);
  }

  parseLineAdv = _line => {
    const line = _line.left || _line;

    const nextLine = _line.right
      ? { ..._line.right, operatorPrec: _line.operator }
      : null;

    return [
      {
        key: uuid(),
        value: line.term,
        condition: this.conditions.find(
          i => i.value === _line.operatorPrec
        ) || { value: '', label: '' },
        in: this.options.find(i => i.value === line.field),
        withCondition: true
      },
      nextLine
    ];
  };

  autoFill = () => {
    const { search } = this.props;
    let criteria = [];

    if (search.adv) {
      const results = parser.parse(search.adv);

      let canParseMore = true;
      let nextLine = results;

      while (canParseMore) {
        const parsed = this.parseLineAdv(nextLine);
        criteria.push(parsed[0]);

        if (parsed[1]) {
          nextLine = parsed[1];
        } else {
          canParseMore = false;
        }
      }

      if (criteria.length > 0) {
        criteria[0].withCondition = false;
      }
    }

    this.setState(
      {
        query: search.query,
        criteria,
        access: search.access,
        platforms: search.platforms,
        years: search.years
      },
      () => this.initializeCriteria()
    );
  };

  initializeCriteria = async () => {
    const { criteria } = this.state;

    if (criteria.length === 0) {
      await this.addCriteria();
      if (!isMobileOnly) await this.addCriteria();
    }
  };

  addCriteria = async () => {
    const { criteria } = this.state;

    const newCriteria = [
      ...criteria,
      {
        key: uuid(),
        condition: null,
        value: '',
        in: this.options[0],
        withCondition: criteria.length > 0
      }
    ];

    return new Promise(resolve =>
      this.setState({ criteria: newCriteria }, () => resolve())
    );
  };

  removeCriteria = (key: string) => () => {
    const { criteria } = this.state;

    const newCriteria = criteria.filter(oneCriteria => oneCriteria.key !== key);

    newCriteria[0].withCondition = false;

    this.setState({ criteria: newCriteria });
  };

  handleCheckbox = (target: string) => (checked: boolean) => {
    const { platforms } = this.state;

    const newPlatforms = {
      ...platforms,
      [target]: checked
    };

    this.setState({ platforms: newPlatforms });
  };

  handleCheckboxAccessOnly = (accessOnly: boolean): void => {
    this.setState({
      access: accessOnly ? ['info:eu-repo/semantics/openAccess'] : []
    });
  };

  getTextField = (
    text: string,
    field: ?SelectType,
    condition: ?SelectType | false,
    withCondition: boolean
  ): string => {
    let textFormatted = text;

    if (field && field.value !== 'all')
      textFormatted = `${field.value}: "${textFormatted}"`;

    if (condition !== false && withCondition) {
      const operator = condition ? condition.value : 'AND';
      textFormatted = `${operator} ${textFormatted}`;
    }

    return textFormatted;
  };

  constructAdvText = (): string => {
    const { criteria } = this.state;

    return criteria
      .filter(({ value }) => value !== '')
      .map(({ value, in: field, condition, withCondition }) =>
        this.getTextField(value, field, condition, withCondition)
      )
      .join(' ');
  };

  handleKeyPress = e => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 13) {
      this.runSearch();
    }
  };

  runSearch = async () => {
    const { query, access, criteria, platforms, years } = this.state;
    const { setCriteria, setDocumentsPerPage } = this.props;

    const adv = this.constructAdvText();

    history.push(`/results${history.location.search}${history.location.hash}`);

    setCriteria({
      query,
      access,
      criteria,
      platforms,
      years,
      author: [],
      type: [],
      adv
    });

    setDocumentsPerPage('12');

    this.props.resetFacetState();
  };

  handleChangeQuery = ({ target: { value: query } }) => {
    this.setState({ query });
  };

  handleChangeYear = (years: any) => {
    this.setState({
      years: {
        ...years,
        mustUse: true
      }
    });
  };

  handleChangeCriteriaSelect = (key: string, target: string) => (
    value: SelectType
  ) => {
    const { criteria } = this.state;

    const newCriteria = criteria.map(oneCritera => {
      return oneCritera.key !== key
        ? oneCritera
        : {
            ...oneCritera,
            [target]: value
          };
    });

    this.setState({ criteria: newCriteria });
  };

  handleChangeCriteriaValue = (key: string) => ({ target: { value } }) => {
    const { criteria } = this.state;

    const newCriteria = criteria.map(oneCritera => {
      return oneCritera.key !== key
        ? oneCritera
        : {
            ...oneCritera,
            value
          };
    });

    this.setState({ criteria: newCriteria });
  };

  render() {
    const {
      query,
      criteria,
      access,
      platforms,
      years,
      screenWidth
    } = this.state;

    return (
      <React.Fragment>
        <SubHeader
          title={formatMessage({ id: 'advancedSearch.advanced_search' })}
          displayHelp={true}
        />

        <Wrapper className="root-container">
          <div className="separator">
            <FormattedMessage id="advancedSearch.in_all_fields" />
          </div>
          <Input
            onChange={this.handleChangeQuery}
            value={query !== '*' ? query : ''}
            className="input"
            onKeyPress={this.handleKeyPress}
            placeholder={formatMessage({ id: 'advancedSearch.terms' })}
            autofocus
          />

          <br />

          <CustomView condition={isMobileOnly}>
            <div className="hr" />
          </CustomView>

          <div className="separator">
            <FormattedMessage id="advancedSearch.add_fields" />
          </div>

          <div className="container-criteria">
            {criteria.map(oneCriteria => (
              <div className="row" key={oneCriteria.key}>
                {oneCriteria.withCondition && (
                  <Select
                    value={oneCriteria.condition || this.conditions[0]}
                    onChange={this.handleChangeCriteriaSelect(
                      oneCriteria.key,
                      'condition'
                    )}
                    className="select2"
                    options={this.conditions}
                  />
                )}
                <Input
                  onChange={this.handleChangeCriteriaValue(oneCriteria.key)}
                  className={oneCriteria.withCondition ? 'input3' : 'input2'}
                  value={oneCriteria.value}
                  placeholder={formatMessage({ id: 'advancedSearch.terms' })}
                  onKeyPress={this.handleKeyPress}
                />
                <Select
                  className="select"
                  onChange={this.handleChangeCriteriaSelect(
                    oneCriteria.key,
                    'in'
                  )}
                  options={this.options}
                  value={oneCriteria.in}
                />
                {criteria.length > 1 ? (
                  <button
                    onClick={this.removeCriteria(oneCriteria.key)}
                    style={{ background: 'transparent', border: 'none' }}
                    className="remove-action"
                    title={formatMessage({ id: 'advancedSearch.remove_line' })}
                    tabIndex={0}
                  >
                    <ReactSVG
                      svgStyle={isMobileOnly ? { width: 32 } : svgStyle}
                      src={close}
                    />
                  </button>
                ) : (
                  <div style={{ minWidth: 52 }} />
                )}
              </div>
            ))}

            <MobileView>
              <br />
            </MobileView>
            <BrowserView>
              <div style={{ minHeight: 18 }} />
            </BrowserView>

            <div className="button-container-search">
              <Button
                onClick={this.addCriteria}
                secondary
                style={{
                  maxWidth: isMobileOnly ? '100%' : 125,
                  minWidth: isMobileOnly ? '100%' : 125,
                  fontSize: 32,
                  fontWeight: 300
                }}
              >
                <FormattedMessage id="advancedSearch.add_criteria" />
              </Button>

              <Button onClick={this.runSearch}>
                <FormattedMessage id="advancedSearch.search" />
              </Button>
            </div>
          </div>

          <div className="separator">
            <FormattedMessage id="advancedSearch.filters" />
          </div>

          <CustomView condition={isMobileOnly}>
            <div className="hr" />
          </CustomView>

          <div className="checkbox-zone">
            <div>
              <div className="title">
                <FormattedMessage id="advancedSearch.document_type" />
              </div>

              <Checkbox
                onClick={this.handleCheckboxAccessOnly}
                value={access.includes('info:eu-repo/semantics/openAccess')}
                label={formatMessage({
                  id: 'advancedSearch.only_access_document'
                })}
              />
            </div>

            <div>
              <div className="title">
                <FormattedMessage id="advancedSearch.platforms" />
              </div>

              {optionsPlatforms.map(option => (
                <Checkbox
                  key={option.value}
                  onClick={this.handleCheckbox(option.value)}
                  value={platforms[option.value]}
                  label={option.label}
                />
              ))}
            </div>

            <div style={{ width: isMobileOnly ? '100%' : '50%' }}>
              <div style={{ maxWidth: 400 }}>
                <div className="title">
                  <FormattedMessage id="advancedSearch.years" />
                </div>

                <YearSelect
                  firstYear={years.min}
                  lastYear={years.max}
                  onChange={this.handleChangeYear}
                  updateInPlace={true}
                  width={
                    screenWidth < 1024
                      ? screenWidth < 400
                        ? '350px'
                        : '240px'
                      : '350px'
                  }
                />
              </div>
            </div>
          </div>

          <div className="hr" />

          <div className="button-container-search">
            <div className="faker" />
            <div className="button-search-zone">
              <Button onClick={this.runSearch}>
                <FormattedMessage id="advancedSearch.search" />
              </Button>
            </div>
          </div>
        </Wrapper>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ search }) => ({
  search
});

const mapDispatchToProps = dispatch => {
  return {
    setCriteria: args => dispatch(setCriteria(args)),
    resetFacetState: () => dispatch(resetFacetState()),
    setDocumentsPerPage: documentsPerPage =>
      dispatch(setDocumentsPerPage(documentsPerPage))
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Result)
);



// WEBPACK FOOTER //
// ./src/pages/AdvancedSearch/index.js